import React from "react";
import { Link } from "react-router-dom";

// Terms of Service. Version must match TERMS_VERSION in functions/index.js so the
// version recorded at acceptance matches what the user actually saw.
export const TERMS_VERSION = "2026-08-06";

const wrap = {
  maxWidth: 780, margin: "0 auto", padding: "2.5em 1.4em 4em", color: "var(--d-ink)",
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  lineHeight: 1.6,
};
const h2 = { fontSize: "1.15em", marginTop: "1.8em", marginBottom: "0.4em" };
const h3 = { fontSize: "1.02em", fontWeight: 700, marginTop: "1.1em", marginBottom: "0.3em", color: "var(--d-ink)" };
const p = { color: "#c7cdd6", margin: "0.5em 0" };

export default function Terms() {
  return (
    <div style={{ background: "var(--d-bg)", minHeight: "100vh" }}>
      <div style={wrap}>
        <p style={{ marginBottom: "1.5em" }}>
          <Link to="/" style={{ color: "var(--link-ink)" }}>← Back to home</Link>
        </p>
        <h1 style={{ fontSize: "1.8em", marginBottom: "0.2em" }}>Terms of Service</h1>
        <p style={{ color: "#9aa0a8" }}>Version {TERMS_VERSION} · Effective {TERMS_VERSION}</p>

        <p style={p}>
          These Terms of Service ("Terms") are a binding agreement between you and the care circle
          you represent ("Customer," "you") and Datagentes LLC ("Datagentes," "we," "us")
          governing your access to and use of the Mary's Bulletin Board application and related
          services (the "Service"). By checking "I agree" or by accessing or using the Service,
          you accept these Terms. If you do not agree, do not use the Service.
        </p>

        <h2 style={h2}>1. Eligibility &amp; accounts</h2>
        <p style={p}>
          You must be at least 18 years old and authorized to bind the Customer to these Terms.
          The individual who creates a care circle is its administrator and is responsible for
          managing Care Circle member access. You are responsible for maintaining the confidentiality of
          your account credentials and for all activity under your account.
        </p>

        <h3 style={h3}>1.1 Care Circle Administrator &amp; User Authorization</h3>
        <p style={p}>
          The individual who creates a care circle is designated as the Account Administrator. The
          Account Administrator retains sole and exclusive responsibility for authorizing, inviting,
          authenticating, and managing all Care Circle members permitted to post messages or content to
          the resident's connected screen. Datagentes LLC has no obligation or technical role in
          verifying the identity or relationship of individuals invited by the Account Administrator.
          You assume full liability for all actions, messages, and content posted by any user added
          to your care circle.
        </p>

        <h2 style={h2}>2. The Service</h2>
        <p style={p}>
          The Service lets Care Circle members post messages and activities that are displayed to
          residents on connected screens. We may update, improve, or modify the Service from time to time. We
          may also establish limits on use and suspend or discontinue features with reasonable
          notice where practicable.
        </p>

        <h2 style={h2}>3. Free trial, subscriptions &amp; billing</h2>
        <p style={p}>
          The Service is offered on a per-family-member-seat subscription. New care circles begin
          with a 30-day free trial. A valid payment method is required to start the trial; you will not be
          charged during the trial and may cancel at any time before it ends to avoid charges.
        </p>
        <p style={p}>
          Unless cancelled, the subscription automatically converts to a paid plan at the end of the
          trial and renews each billing period. The amount billed is the per-seat price multiplied
          by the number of family-member seats (including the administrator) in your care circle at
          the time of billing. Adding or removing family members changes the number of seats and the amount
          billed. Fees are billed in advance, are non-refundable except as required by law, and are
          exclusive of taxes, which you are responsible for. You may cancel at any time through the
          billing portal; cancellation stops future renewals and access continues until the end of
          the current paid period. Payments are processed by Stripe, Inc.; by using the Service you
          also agree to Stripe's applicable terms.
        </p>

        <h2 style={h2}>4. Customer data, privacy &amp; HIPAA</h2>
        <p style={p}>
          As between the parties, you own the content you and your Care Circle members submit ("Customer
          Data"). You grant us a limited license to host and process Customer Data solely to provide
          and support the Service. You are responsible for the accuracy and legality of Customer
          Data and for obtaining any consents required to submit it.
        </p>
        <p style={p}>
          To the extent Customer Data includes protected health information ("PHI") as defined by
          HIPAA, our handling of that PHI is governed by a Business Associate Agreement ("BAA"). The
          Service runs on HIPAA-eligible infrastructure under a BAA with our cloud provider. You are
          responsible for using the Service in compliance with HIPAA and applicable law, including
          limiting the PHI you submit to what is appropriate for a resident-facing display.
        </p>

        <h2 style={h2}>5. Acceptable use</h2>
        <p style={p}>
          You agree not to: (a) use the Service unlawfully or to infringe others' rights; (b) upload
          malicious code or attempt to breach security or access data that is not yours; (c) share
          access with anyone outside your care circle or exceed your licensed seats through deceptive
          means; or (d) interfere with or disrupt the integrity or performance of the Service.
        </p>

        <h3 style={h3}>5.1 Intended vs. Discouraged Messaging Usage</h3>
        <p style={p}>
          The Service is designed strictly as a passive, one-way visual communication tool to share
          general family updates, personal messages, schedules, and photos with residents.
        </p>
        <p style={p}>
          <strong>Prohibited Content:</strong> You agree that no user will submit, post, or transmit
          Protected Health Information (PHI) as defined under HIPAA regulations. Care Circle members are
          strictly responsible for ensuring that messages do not contain medical chart notes,
          treatment plans, clinical diagnostic information, or sensitive personal health details.
        </p>
        <p style={p}>
          <strong>Discouraged Usage:</strong> The Service must not be used to coordinate
          time-sensitive medical care, log daily nursing requirements, or deliver urgent messages
          that require immediate action or confirmation from facility staff.
        </p>
        <p style={p}>
          <strong>Hardware Warning:</strong> This system is not a medical alert device, emergency
          call button, or life-safety system. In the event of a medical emergency, you must contact
          local emergency services (911) or the facility's administrative staff directly through
          traditional telecommunication channels. Datagentes LLC disclaims all liability resulting
          from delayed, dropped, or un-synced messages containing urgent information.
        </p>

        <h2 style={h2}>6. Intellectual property</h2>
        <p style={p}>
          The Service, including its software, design, and trademarks, is owned by Datagentes and
          its licensors and is protected by law. Except for the rights expressly granted here, we
          reserve all rights. You may not copy, modify, reverse engineer, or create derivative works
          of the Service.
        </p>

        <h2 style={h2}>7. Disclaimers</h2>
        <p style={p}>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not warrant that the Service will be
          uninterrupted or error-free. The Service is a communication tool and is not a medical
          device or a substitute for professional judgment or care.
        </p>

        <h2 style={h2}>8. Limitation of liability</h2>
        <p style={p}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR DATA.
          OUR TOTAL LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT EXCEED THE AMOUNTS
          YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE
          CLAIM. Nothing limits liability that cannot be limited under applicable law.
        </p>

        <h2 style={h2}>9. Indemnification</h2>
        <p style={p}>
          You will defend and indemnify Datagentes against third-party claims arising from your
          Customer Data, your use of the Service in violation of these Terms, or your violation of
          applicable law.
        </p>

        <h2 style={h2}>10. Term &amp; termination</h2>
        <p style={p}>
          These Terms apply while you use the Service. Either party may terminate for material
          breach not cured within 30 days of notice. We may suspend access for non-payment or for
          conduct that risks harm to the Service or others. Upon termination, your right to use the
          Service ends; we may delete Customer Data after a reasonable period unless retention is
          required by law or a BAA.
        </p>

        <h2 style={h2}>11. Changes to these Terms</h2>
        <p style={p}>
          We may update these Terms from time to time. Material changes will be posted here with a
          new version and effective date, and where appropriate we will ask you to re-accept.
          Continued use after changes take effect constitutes acceptance.
        </p>

        <h2 style={h2}>12. Governing law</h2>
        <p style={p}>
          These Terms are governed by the laws of the State of Delaware, without regard to its
          conflict-of-laws rules, and the parties consent to the exclusive jurisdiction of the
          state and federal courts located there, except where applicable law requires otherwise.
        </p>

        <h2 style={h2}>13. Contact</h2>
        <p style={p}>
          Questions about these Terms: Datagentes LLC — support@Datagentes.com.
        </p>
      </div>
    </div>
  );
}
